"use client"

import { useState, useEffect, useMemo, useRef } from "react"
import {
  buildAerationPlan,
  A_GRASSES,
  A_TRAFFIC,
  A_LAST,
  A_METHODS,
  GRASS_ID_TO_ENGINE_NAME,
  GRASS_NAME_TO_ID,
  grassDisplayName,
  type GrassId,
  type TrafficLevel,
  type LastAeratedId,
  type MethodId,
} from "@/lib/aeration-plan"
import {
  calculateAerationTiming,
  type AerationResult,
} from "@/lib/aeration-calculator"
import { setBottomDockHeight, useAdDockInset } from "@/hooks/use-bottom-dock-inset"
import { useCalculatorAnalytics } from "@/hooks/useCalculatorAnalytics"
import { useSavedInputs } from "@/hooks/use-saved-inputs"
import { getRegionFromZipCode, REGION_DEFAULT_GRASS, toLegacyRegion } from "@/data/regional-climate"
import { ZipCodeInput } from "@/components/ui/zip-code-input"
import { AerationPreview } from "@/components/tools/aeration-preview"

const A_BG = "#ffffff"
const A_INK = "#111827"
const A_INK2 = "#374151"
const A_RULE = "#e5e7eb"
const A_BRAND = "#16a34a"
const A_BRAND_700 = "#15803d"
const A_BRAND_50 = "#f0fdf4"
const A_BRAND_100 = "#dcfce7"
const A_MUTED = "#6b7280"
const A_PAPER_DEEP = "#f3f4f6" // gray-100 — faint backdrop behind the carded sections
const A_FONT = `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`

// ── Inline icons ─────────────────────────────────────────────────
function Ico({ size = 14, children }: { size?: number; children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  )
}
const IcoCal = (p: { size?: number }) => (
  <Ico {...p}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 9h18M8 3v4M16 3v4" />
  </Ico>
)
const IcoLeaf = (p: { size?: number }) => (
  <Ico {...p}>
    <path d="M5 19c8 0 14-6 14-14C9 5 5 11 5 19z" />
    <path d="M5 19l9-9" />
  </Ico>
)
const IcoPin = (p: { size?: number }) => (
  <Ico {...p}>
    <path d="M12 21s-7-7.5-7-12a7 7 0 0114 0c0 4.5-7 12-7 12z" />
    <circle cx="12" cy="9" r="2.5" />
  </Ico>
)
const IcoFoot = (p: { size?: number }) => (
  <Ico {...p}>
    <path d="M9 4c2 0 3 2 3 5s-2 5-3 5-3-1-3-5 1-5 3-5z" />
    <circle cx="14.5" cy="7.5" r="1" />
    <circle cx="17" cy="9.5" r="1" />
    <circle cx="17.5" cy="12.5" r="1" />
    <circle cx="15.5" cy="14.5" r="1" />
    <path d="M6 18c0 2 1 3 3 3s3-1 3-3" />
  </Ico>
)
const IcoDrill = (p: { size?: number }) => (
  <Ico {...p}>
    <rect x="9" y="3" width="6" height="3" rx="0.5" />
    <path d="M10 6v3M14 6v3" />
    <path d="M9 9h6v3l-3 9-3-9z" />
  </Ico>
)
const IcoChev = (p: { size?: number }) => <Ico {...p}><path d="M6 9l6 6 6-6" /></Ico>
const IcoArrow = (p: { size?: number }) => (
  <Ico {...p}>
    <path d="M5 12h14" />
    <path d="M13 6l6 6-6 6" />
  </Ico>
)
const IcoLock = (p: { size?: number }) => (
  <Ico {...p}>
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 018 0v4" />
  </Ico>
)

function GrassGlyph({ active, season }: { active: boolean; season: "cool" | "warm" }) {
  const ink = active ? A_BRAND : A_INK2
  return (
    <span
      style={{
        width: 36,
        height: 36,
        borderRadius: 18,
        background: active ? A_BRAND_100 : A_BRAND_50,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke={ink}
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M4 21h16" />
        <path d="M7 21c0-5 1-8 3-10" />
        <path d="M12 21c0-6 1-9 3-12" />
        <path d="M17 21c0-4 0-7 2-9" />
        {season === "cool" && <circle cx="6" cy="9" r="1.2" fill={ink} stroke="none" />}
      </svg>
    </span>
  )
}

function MethodGlyph({ id, active }: { id: MethodId; active: boolean }) {
  const ink = active ? A_BRAND : A_INK2
  return (
    <span
      style={{
        width: 36,
        height: 36,
        borderRadius: 18,
        background: active ? A_BRAND_100 : A_BRAND_50,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {id === "core" && (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke={ink}
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M3 16h18" />
          <rect x="6" y="6" width="3" height="10" rx="1" />
          <rect x="11" y="9" width="3" height="7" rx="1" />
          <rect x="16" y="7" width="3" height="9" rx="1" />
        </svg>
      )}
      {id === "spike" && (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke={ink}
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M3 16h18" />
          <path d="M7 16V6M12 16V4M17 16V7" />
          <path d="M5 16l2 2 2-2M10 16l2 2 2-2M15 16l2 2 2-2" opacity="0.5" />
        </svg>
      )}
      {id === "liquid" && (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke={ink}
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 3s5 5 5 9a5 5 0 11-10 0c0-4 5-9 5-9z" />
          <path d="M9 14a3 3 0 003 3" opacity="0.6" />
        </svg>
      )}
    </span>
  )
}

function StepCircle({ step, accent }: { step: number; accent?: boolean }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 27,
        height: 27,
        flexShrink: 0,
        borderRadius: "50%",
        background: accent ? A_BRAND_50 : A_PAPER_DEEP,
        border: accent ? `1.5px solid ${A_BRAND}` : `1px solid ${A_RULE}`,
        boxShadow: accent ? `0 0 0 3px ${A_BRAND}22` : "none",
        color: accent ? A_BRAND_700 : A_INK,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 13,
        fontWeight: 700,
        lineHeight: 1,
      }}
    >
      {step}
    </span>
  )
}

function Label({
  icon,
  children,
  hint,
  step,
  accent,
}: {
  icon?: React.ReactNode
  children: React.ReactNode
  hint?: React.ReactNode
  step?: number
  accent?: boolean
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        marginBottom: 10,
        gap: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        {step != null && <StepCircle step={step} accent={accent} />}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: A_BRAND_700,
          }}
        >
          {icon}
          {children}
        </div>
      </div>
      {hint && (
        <div style={{ fontSize: 13, color: A_MUTED, textAlign: "right" }}>{hint}</div>
      )}
    </div>
  )
}

function SectionCard({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${A_RULE}`,
        borderRadius: 12,
        padding: 18,
      }}
    >
      {children}
    </div>
  )
}

const GRASS_RECOMMENDED: GrassId[] = ["fescue", "kbg", "bermuda", "zoysia"]

function GrassField({
  value,
  onChange,
}: {
  value: GrassId
  onChange: (v: GrassId) => void
}) {
  const [showAll, setShowAll] = useState(false)
  const all = Object.keys(A_GRASSES) as GrassId[]
  const list = showAll ? all : GRASS_RECOMMENDED
  return (
    <div>
      <Label step={2} icon={<IcoLeaf size={13} />} hint={A_GRASSES[value].tag}>
        Grass type
      </Label>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 }}>
        {list.map((id) => {
          const g = A_GRASSES[id]
          const active = id === value
          return (
            <button
              type="button"
              key={id}
              onClick={() => onChange(id)}
              aria-pressed={active}
              style={{
                padding: "10px 12px",
                background: active ? A_BRAND_50 : "#fff",
                color: A_INK,
                border: `1px solid ${active ? A_BRAND : A_RULE}`,
                borderRadius: 8,
                cursor: "pointer",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                gap: 10,
                transition: "all 150ms var(--ease-standard)",
              }}
            >
              <span className="hidden xs:inline-flex">
                <GrassGlyph active={active} season={g.season} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    overflowWrap: "break-word",
                  }}
                >
                  {grassDisplayName(g.name)}
                </div>
                <div style={{ fontSize: 12, color: A_MUTED, marginTop: 1 }}>
                  {g.tag} · {g.optimalSoil}
                </div>
              </div>
            </button>
          )
        })}
      </div>
      <button
        type="button"
        onClick={() => setShowAll((visible) => !visible)}
        style={{
          marginTop: 10,
          background: "transparent",
          border: "none",
          padding: "4px 0",
          fontSize: 13,
          color: A_BRAND,
          fontWeight: 500,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        {showAll ? "Show fewer" : `+${all.length - GRASS_RECOMMENDED.length} more grass types`}{" "}
        <span style={{ transform: showAll ? "rotate(180deg)" : undefined }}>
          <IcoChev size={12} />
        </span>
      </button>
    </div>
  )
}

function TrafficField({
  value,
  onChange,
}: {
  value: TrafficLevel
  onChange: (v: TrafficLevel) => void
}) {
  return (
    <div>
      <Label step={3} icon={<IcoFoot size={13} />} hint="how compacted is the soil">
        Foot traffic
      </Label>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          background: "#fff",
          border: `1px solid ${A_RULE}`,
          borderRadius: 8,
          padding: 4,
          gap: 4,
        }}
      >
        {(Object.entries(A_TRAFFIC) as [TrafficLevel, (typeof A_TRAFFIC)[TrafficLevel]][]).map(
          ([id, t]) => {
            const active = id === value
            return (
              <button
                type="button"
                key={id}
                onClick={() => onChange(id)}
                aria-pressed={active}
                style={{
                  padding: "10px 8px",
                  background: active ? A_BRAND : "transparent",
                  color: active ? "#fff" : A_INK,
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: 13.5, fontWeight: 600 }}>{t.name}</div>
                <div
                  style={{
                    fontSize: 10.5,
                    opacity: active ? 0.85 : 0.6,
                    marginTop: 2,
                  }}
                >
                  every {t.due} mo
                </div>
              </button>
            )
          },
        )}
      </div>
      <div style={{ fontSize: 12, color: A_MUTED, marginTop: 8 }}>
        {A_TRAFFIC[value].blurb}
      </div>
    </div>
  )
}

function LastField({
  value,
  onChange,
}: {
  value: LastAeratedId
  onChange: (v: LastAeratedId) => void
}) {
  return (
    <div>
      <Label step={4} icon={<IcoCal size={13} />}>Last aerated</Label>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {A_LAST.map((opt) => {
          const active = opt.id === value
          return (
            <button
              type="button"
              key={opt.id}
              onClick={() => onChange(opt.id)}
              aria-pressed={active}
              style={{
                padding: "8px 14px",
                background: active ? A_BRAND : "#fff",
                color: active ? "#fff" : A_INK2,
                border: `1px solid ${active ? A_BRAND : A_RULE}`,
                borderRadius: 9999,
                fontSize: 13,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              {opt.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function MethodField({
  value,
  onChange,
}: {
  value: MethodId
  onChange: (v: MethodId) => void
}) {
  return (
    <div>
      <Label step={5} icon={<IcoDrill size={13} />}>Aeration method</Label>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {(Object.entries(A_METHODS) as [MethodId, (typeof A_METHODS)[MethodId]][]).map(
          ([id, m]) => {
            const active = id === value
            return (
              <button
                type="button"
                key={id}
                onClick={() => onChange(id)}
                aria-pressed={active}
                style={{
                  padding: "10px 12px",
                  background: active ? A_BRAND_50 : "#fff",
                  color: A_INK,
                  border: `1px solid ${active ? A_BRAND : A_RULE}`,
                  borderRadius: 8,
                  cursor: "pointer",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  transition: "all 150ms var(--ease-standard)",
                }}
              >
                <MethodGlyph id={id} active={active} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>{m.name}</span>
                    {m.best && (
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: "0.05em",
                          textTransform: "uppercase",
                          color: A_BRAND_700,
                          background: A_BRAND_100,
                          padding: "2px 7px",
                          borderRadius: 9999,
                        }}
                      >
                        Recommended
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: A_MUTED, marginTop: 2 }}>{m.blurb}</div>
                </div>
              </button>
            )
          },
        )}
      </div>
    </div>
  )
}

// ── Form ──────────────────────────────────────────────────────────
const A_REGION_TO_GRASS: Record<string, GrassId> = {
  Southeast: "bermuda",
  "South Central": "bermuda",
  Northeast: "fescue",
  "Great Lakes": "kbg",
  "Upper Midwest": "kbg",
  "Mountain West": "kbg",
  "Mid-Atlantic": "fescue",
  Central: "fescue",
  "West Coast": "fescue",
}

export interface AerationFormSubmitData {
  zipCode: string
  grassType: string
  traffic: TrafficLevel
  lastAerated: LastAeratedId
  method: MethodId
}

interface AerationFormProps {
  initialZip?: string
  initialGrass?: string
  initialLawnSize?: number
  initialSunlight?: string
  /** Called whenever the live engine result changes, so the page can render
   * downstream UI (like the data chart) without duplicating the engine call. */
  onResultChange?: (result: AerationResult | null) => void
}

export function AerationForm({
  initialZip,
  initialGrass,
  initialLawnSize,
  initialSunlight,
  onResultChange,
}: AerationFormProps) {
  const isLoading = false
  const error: string | null = null
  const [zip, setZip] = useState<string>(initialZip ?? "")
  const [grass, setGrass] = useState<GrassId>(() => {
    if (initialGrass && GRASS_NAME_TO_ID[initialGrass]) return GRASS_NAME_TO_ID[initialGrass]
    return "fescue"
  })
  const [traffic, setTraffic] = useState<TrafficLevel>("med")
  const [last, setLast] = useState<LastAeratedId>("1y")
  const [method, setMethod] = useState<MethodId>("core")

  const region = useMemo(() => getRegionFromZipCode(zip), [zip])
  const zipKnown = zip.length === 5 && region !== "Unknown"

  const { savedInputs, saveInputs } = useSavedInputs()
  const seededFromZip = useRef(false)
  const seededFromSaved = useRef(false)

  useEffect(() => {
    if (initialZip && initialZip !== zip) {
      setZip(initialZip)
    }
  }, [initialZip, zip])

  useEffect(() => {
    if (!initialGrass) return
    const nextGrass = GRASS_NAME_TO_ID[initialGrass]
    if (!nextGrass) return
    setGrass(nextGrass)
    seededFromZip.current = true
    seededFromSaved.current = true
  }, [initialGrass])

  // Cross-site savedInputs takes precedence over zip-based defaults.
  useEffect(() => {
    if (seededFromSaved.current) return
    const saved = savedInputs.grassType
    if (saved && GRASS_NAME_TO_ID[saved]) {
      setGrass(GRASS_NAME_TO_ID[saved])
      seededFromZip.current = true
      seededFromSaved.current = true
    }
  }, [savedInputs.grassType])

  // Auto-set grass when ZIP resolves to a known region (only if user hasn't
  // explicitly picked yet — tracked by a ref).
  useEffect(() => {
    if (!zipKnown || seededFromZip.current) return
    const def = REGION_DEFAULT_GRASS[region]
    if (def && GRASS_NAME_TO_ID[def]) {
      setGrass(GRASS_NAME_TO_ID[def])
    } else if (A_REGION_TO_GRASS[toLegacyRegion(region)]) {
      // A_REGION_TO_GRASS is keyed on the legacy region set; degrade fine-grained
      // climate sub-regions (California, Florida, etc.) to their legacy parent.
      setGrass(A_REGION_TO_GRASS[toLegacyRegion(region)])
    }
    seededFromZip.current = true
  }, [zipKnown, region])

  const plan = useMemo(
    () => buildAerationPlan({ grass, traffic, last }),
    [grass, traffic, last],
  )

  const liveResult = useMemo<AerationResult | null>(() => {
    if (!zipKnown) return null
    try {
      return calculateAerationTiming({
        grassType: GRASS_ID_TO_ENGINE_NAME[grass],
        zipCode: zip,
        lawnSize: initialLawnSize,
        sunlight: initialSunlight ?? savedInputs.sunlight,
      })
    } catch {
      return null
    }
  }, [grass, zip, zipKnown, initialLawnSize, initialSunlight, savedInputs.sunlight])

  useEffect(() => {
    onResultChange?.(liveResult)
  }, [liveResult, onResultChange])

  useCalculatorAnalytics(
    'aeration',
    zipKnown
      ? {
          inputs: {
            zip_code: zip,
            grass_type: grass,
            traffic,
            last_aerated: last,
          },
          result: {
            region,
            best_window_start: plan.bestStart.toISOString().slice(0, 10),
            best_window_end: plan.bestEnd.toISOString().slice(0, 10),
            status: plan.status,
            in_window_now: plan.inWindowNow,
            overdue: plan.overdue,
          },
          location: zip,
          source: 'live_update',
        }
      : null,
  )

  // Stacked layout through tablet portrait; the desktop preview rail needs room.
  const [narrow, setNarrow] = useState(false)
  const [phone, setPhone] = useState(false)
  const [formInView, setFormInView] = useState(true)
  const formRef = useRef<HTMLFormElement | null>(null)
  const dockRef = useRef<HTMLDivElement | null>(null)
  // Clears the Mediavine anchor/player beneath this bar. Must be the ad-only
  // inset, not the dock total, which includes this bar's own height.
  const adDockInset = useAdDockInset()
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)")
    const sync = () => setNarrow(mq.matches)
    sync()
    mq.addEventListener("change", sync)
    return () => mq.removeEventListener("change", sync)
  }, [])

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)")
    const sync = () => setPhone(mq.matches)
    sync()
    mq.addEventListener("change", sync)
    return () => mq.removeEventListener("change", sync)
  }, [])

  useEffect(() => {
    if (!phone) {
      setFormInView(true)
      return
    }
    const el = formRef.current
    if (!el || typeof IntersectionObserver === "undefined") return
    const observer = new IntersectionObserver(
      ([entry]) => setFormInView(entry.isIntersecting),
      { rootMargin: "0px 0px -25% 0px", threshold: 0 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [phone])

  const showDock = phone && formInView

  useEffect(() => {
    if (!showDock) {
      setBottomDockHeight(0)
      return
    }
    const el = dockRef.current
    if (!el) return
    const ro = new ResizeObserver(() => {
      setBottomDockHeight(el.getBoundingClientRect().height)
    })
    ro.observe(el)
    setBottomDockHeight(el.getBoundingClientRect().height)
    return () => {
      ro.disconnect()
      setBottomDockHeight(0)
    }
  }, [showDock])

  // The form is fully live; the submit handler now just absorbs Enter
  // keypresses to keep <form> autofill semantics intact.
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
  }

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      style={{
        background: A_BG,
        color: A_INK,
        fontFamily: A_FONT,
        borderRadius: 18,
        border: `1px solid ${A_RULE}`,
        boxShadow: "0 12px 40px rgba(0,0,0,0.04)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: narrow ? "minmax(0,1fr)" : "minmax(0,1fr) 420px",
          gap: narrow ? 24 : 32,
          padding: narrow ? "20px 18px 100px" : "32px 32px 36px",
        }}
      >
        {/* Mobile: live preview first */}
        {narrow && <AerationPreview plan={plan} compact />}

        {/* Form column */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 14,
            background: A_PAPER_DEEP,
            borderRadius: 14,
            padding: 12,
          }}
        >
          <SectionCard>
            <Label
              step={1}
              accent
              icon={<IcoPin size={13} />}
              hint={zipKnown ? `climate zone matched: ${region}` : "sets your climate zone"}
            >
              Where
            </Label>
            <ZipCodeInput
              value={zip}
              onChange={setZip}
              error={
                zip.length === 5 && region === "Unknown"
                  ? "We don't recognize that ZIP. Showing timing by grass type."
                  : undefined
              }
              showLabel={false}
              enableGeolocation
              rememberLastZip
            />
          </SectionCard>

          <SectionCard>
            <GrassField
              value={grass}
              onChange={(g) => {
                seededFromZip.current = true
                setGrass(g)
                saveInputs({ grassType: GRASS_ID_TO_ENGINE_NAME[g] })
              }}
            />
          </SectionCard>

          <SectionCard>
            <TrafficField value={traffic} onChange={setTraffic} />
          </SectionCard>

          <SectionCard>
            <LastField value={last} onChange={setLast} />
          </SectionCard>

          <SectionCard>
            <MethodField value={method} onChange={setMethod} />
          </SectionCard>

          {error && (
            <div
              role="alert"
              style={{
                fontSize: 13,
                color: "#b04531",
                background: "#fff5f3",
                border: "1px solid #f5d4cd",
                borderRadius: 8,
                padding: "10px 12px",
              }}
            >
              {error}
            </div>
          )}
        </div>

        {/* Desktop right rail */}
        {!narrow && (
          <aside
            style={{
              alignSelf: "flex-start",
              position: "sticky",
              top: 24,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <AerationPreview plan={plan} />
            <div
              style={{
                fontSize: 12,
                color: A_MUTED,
                lineHeight: 1.55,
                display: "flex",
                gap: 8,
                padding: "0 4px",
              }}
            >
              <span style={{ flexShrink: 0, marginTop: 2 }}>
                <IcoLock size={12} />
              </span>
              <span>Windows from university extension data, last reviewed May 2026.</span>
            </div>
          </aside>
        )}
      </div>

      {/* Mobile sticky bottom dock */}
      {showDock && (
        <div
          ref={dockRef}
          style={{
            position: "fixed",
            bottom: adDockInset,
            left: 0,
            right: 0,
            zIndex: 30,
            boxShadow: "0 -8px 30px rgba(0,0,0,0.08)",
            background: "#fff",
            borderTop: `1px solid ${A_RULE}`,
            padding: "12px 16px",
            paddingBottom: "calc(12px + env(safe-area-inset-bottom))",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: A_BRAND_700,
                marginBottom: 2,
              }}
            >
              Best window
            </div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: A_INK,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {plan.bestStart && plan.bestEnd
                ? `${plan.bestStart.toLocaleString("en", {
                    month: "short",
                    day: "numeric",
                  })} – ${plan.bestEnd.toLocaleString("en", { month: "short", day: "numeric" })}`
                : "—"}
            </div>
          </div>
        </div>
      )}
    </form>
  )
}
